import { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Plus, Search, Store, X, ChevronRight } from 'lucide-react';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetTrigger, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { OwnerPopoverContent, OwnerSheetContent } from '@/components/owner-ui';
import { useShop } from '@/contexts/ShopContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ownerPath } from '@/lib/hosts';
import { cn } from '@/lib/utils';
import { getShopColorPalette, initials, formatRole } from './shopSwitcherUtils';

// ============================================================
// TYPES
// ============================================================
export interface ShopSwitcherProps {
  /**
   * 'rail': bouton compact pour la barre latérale desktop (avatar + statut).
   * 'mobile-header': capsule élégante pour l'en-tête du drawer mobile.
   * 'drawer': ligne pleine largeur pour le drawer mobile simple.
   */
  variant?: 'rail' | 'mobile-header' | 'drawer';
  onNavigate?: () => void;
  className?: string;
}

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
export function ShopSwitcher({ variant = 'rail', onNavigate, className }: ShopSwitcherProps) {
  const { shops, currentShop, selectShop } = useShop();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation('navigation');

  // Reset search when opening/closing
  useEffect(() => {
    if (!open) {
      setSearch('');
    } else {
      // Focus search on open after transition
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Filtrage des boutiques
  const filteredShops = useMemo(() => {
    if (!search.trim()) return shops;
    const query = search.toLowerCase().trim();
    return shops.filter((s) => s.name.toLowerCase().includes(query));
  }, [shops, search]);

  const activeShopData = useMemo(() => {
    return shops.find((s) => s.id === currentShop?.id) ?? currentShop;
  }, [shops, currentShop]);

  const otherShops = useMemo(() => {
    return filteredShops.filter((s) => s.id !== currentShop?.id);
  }, [filteredShops, currentShop?.id]);

  const isActiveInFilter = useMemo(() => {
    if (!currentShop) return false;
    if (!search.trim()) return true;
    return currentShop.name.toLowerCase().includes(search.toLowerCase().trim());
  }, [currentShop, search]);

  const handleSelectShop = (shop: (typeof shops)[0]) => {
    if (currentShop?.id !== shop.id) {
      selectShop(shop);
    }
    setOpen(false);
    onNavigate?.();
  };

  const handleActionClick = () => {
    setOpen(false);
    onNavigate?.();
  };

  const currentPalette = getShopColorPalette(activeShopData?.name ?? 'Velko');

  // ============================================================
  // BOUTONS DÉCLENCHEURS (TRIGGERS)
  // ============================================================
  const triggerButton = (() => {
    if (variant === 'mobile-header') {
      return (
        <button
          type="button"
          aria-label={t('navigation:shop_switcher', {
            defaultValue: 'Changer de boutique',
          })}
          className={cn(
            'group -ml-1 mt-1 inline-flex max-w-full items-center gap-2 rounded-lg border border-slate-200/90 bg-slate-50/90 px-2.5 py-1.5 text-left transition-all',
            'hover:border-slate-300 hover:bg-slate-100/80 dark:border-charcoal-700/80 dark:bg-charcoal-800/80 dark:hover:border-charcoal-600 dark:hover:bg-charcoal-750',
            className,
          )}
        >
          <span
            className={cn(
              'grid h-5 w-5 shrink-0 place-items-center rounded-md font-head text-[9px] font-bold',
              currentPalette.bg,
              currentPalette.text,
            )}
          >
            {initials(activeShopData?.name)}
          </span>
          <div className="min-w-0 flex-1 truncate">
            <p className="truncate font-head text-[12px] font-bold leading-none text-charcoal-950 dark:text-slate-100">
              {activeShopData?.name ?? t('navigation:shops_section', { defaultValue: 'Boutique' })}
            </p>
          </div>
          <ChevronsUpDown
            className="h-3.5 w-3.5 shrink-0 text-slate-400 transition-colors group-hover:text-charcoal-900 dark:text-slate-500 dark:group-hover:text-slate-200"
            strokeWidth={2}
            aria-hidden="true"
          />
        </button>
      );
    }

    if (variant === 'drawer') {
      return (
        <button
          type="button"
          aria-label={t('navigation:shop_switcher', {
            defaultValue: 'Changer de boutique',
          })}
          className={cn(
            'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-charcoal-800',
            className,
          )}
        >
          <span
            className={cn(
              'grid h-8 w-8 place-items-center rounded-lg font-head text-[11px] font-bold',
              currentPalette.bg,
              currentPalette.text,
            )}
          >
            {initials(activeShopData?.name)}
          </span>
          <div className="min-w-0 flex-1 text-left">
            <p className="truncate font-semibold text-charcoal-950 dark:text-slate-100">
              {activeShopData?.name ?? t('navigation:shops_section', { defaultValue: 'Boutique' })}
            </p>
            {activeShopData?.role && (
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                {formatRole(activeShopData.role)}
              </p>
            )}
          </div>
          <ChevronsUpDown className="h-4 w-4 text-slate-400" />
        </button>
      );
    }

    // Rail mode (desktop default)
    return (
      <button
        type="button"
        aria-label={t('navigation:shop_switcher', {
          defaultValue: 'Changer de boutique',
        })}
        title={`${t('navigation:shop_switcher', { defaultValue: 'Changer de boutique' })} : ${activeShopData?.name ?? ''}`}
        className={cn(
          'group/shop relative flex h-10 w-10 items-center justify-center rounded-xl p-0.5 transition-transform hover:scale-105 active:scale-95',
          className,
        )}
      >
        <span
          className={cn(
            'grid h-full w-full place-items-center rounded-[10px] font-head text-[11px] font-bold shadow-xs transition-colors',
            currentPalette.bg,
            currentPalette.text,
            'ring-1 ring-black/5 dark:ring-white/10',
          )}
        >
          {initials(activeShopData?.name)}
        </span>
        {/* Pastille active */}
        <span
          aria-hidden="true"
          className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-lime-500 shadow-xs dark:border-charcoal-900"
        />
      </button>
    );
  })();

  // ============================================================
  // CONTENU INTERNE DU SÉLECTEUR (LISTE & CARTES)
  // ============================================================
  const listContent = (
    <div className="flex flex-col">
      {/* Barre de recherche (si 3 boutiques ou plus) */}
      {shops.length >= 3 && (
        <div className="relative mb-2 px-1">
          <Search
            className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
            strokeWidth={2}
          />
          <input
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('navigation:search_shop', {
              defaultValue: 'Rechercher une boutique...',
            })}
            className="w-full rounded-lg border border-slate-200 bg-slate-50/80 py-1.5 pl-8 pr-8 text-[12px] text-charcoal-950 placeholder:text-slate-400 focus:border-lime-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-lime-500/20 dark:border-charcoal-700 dark:bg-charcoal-800/80 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:bg-charcoal-850"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Effacer la recherche"
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-charcoal-950 dark:hover:bg-charcoal-700 dark:hover:text-slate-200"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}

      {/* Section Boutique actuelle */}
      {isActiveInFilter && activeShopData && (
        <div className="mb-2 px-1">
          <p className="mb-1 px-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
            {t('navigation:current_shop', { defaultValue: 'Boutique actuelle' })}
          </p>
          <div className="flex items-center gap-3 rounded-xl border border-lime-500/40 bg-lime-50/80 p-2.5 shadow-xs dark:border-lime-500/30 dark:bg-lime-500/10">
            <span
              className={cn(
                'grid h-9 w-9 shrink-0 place-items-center rounded-lg font-head text-[12px] font-bold shadow-xs',
                currentPalette.bg,
                currentPalette.text,
              )}
            >
              {initials(activeShopData.name)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <p className="truncate text-[13px] font-bold text-charcoal-950 dark:text-slate-50">
                  {activeShopData.name}
                </p>
              </div>
              <div className="mt-0.5 flex items-center gap-1.5">
                {activeShopData.role && (
                  <span className="inline-block rounded-full bg-lime-200/70 px-2 py-0.5 text-[10px] font-semibold text-lime-800 dark:bg-lime-500/20 dark:text-lime-300">
                    {formatRole(activeShopData.role)}
                  </span>
                )}
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-lime-700 dark:text-lime-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-lime-500 animate-pulse" />
                  {t('navigation:active_badge', { defaultValue: 'Actuelle' })}
                </span>
              </div>
            </div>
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-lime-500 text-white shadow-xs">
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
            </span>
          </div>
        </div>
      )}

      {/* Section Autres boutiques */}
      {otherShops.length > 0 && (
        <div className="px-1">
          <p className="mb-1 mt-1 px-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
            {t('navigation:other_shops', { defaultValue: 'Autres boutiques' })}{' '}
            <span className="font-normal opacity-70">({otherShops.length})</span>
          </p>
          <ul
            role="listbox"
            aria-label={t('navigation:other_shops', { defaultValue: 'Autres boutiques' })}
            className="flex max-h-56 flex-col gap-1 overflow-y-auto pr-0.5"
          >
            {otherShops.map((shop) => {
              const palette = getShopColorPalette(shop.name);
              return (
                <li key={shop.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    onClick={() => handleSelectShop(shop)}
                    className={cn(
                      'group flex w-full items-center gap-3 rounded-xl p-2 text-left transition-all',
                      'hover:bg-slate-100 hover:shadow-xs active:scale-[0.99] dark:hover:bg-charcoal-800',
                    )}
                  >
                    <span
                      className={cn(
                        'grid h-8 w-8 shrink-0 place-items-center rounded-lg font-head text-[11px] font-bold transition-transform group-hover:scale-105',
                        palette.bg,
                        palette.text,
                      )}
                    >
                      {initials(shop.name)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-medium text-charcoal-900 group-hover:text-charcoal-950 dark:text-slate-200 dark:group-hover:text-slate-50">
                        {shop.name}
                      </p>
                      {shop.role && (
                        <p className="text-[10px] text-slate-400 dark:text-slate-500">
                          {formatRole(shop.role)}
                        </p>
                      )}
                    </div>
                    <ChevronRight
                      className="h-4 w-4 shrink-0 text-slate-300 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100 dark:text-slate-600"
                      strokeWidth={2}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* État vide si la recherche n'a aucun résultat */}
      {filteredShops.length === 0 && (
        <div className="py-6 text-center text-[12px] text-slate-500 dark:text-slate-400">
          <p>{t('navigation:no_shop_found', { defaultValue: 'Aucune boutique trouvée' })}</p>
          <p className="mt-0.5 text-[11px] text-slate-400">« {search} »</p>
        </div>
      )}

      {/* Séparateur */}
      <div className="my-2 border-t border-slate-200/80 dark:border-charcoal-700/80" />

      {/* Actions en bas */}
      <div className="flex flex-col gap-1 px-1">
        <Link
          to={ownerPath('/shops?create=1')}
          onClick={handleActionClick}
          className="flex items-center justify-center gap-2 rounded-lg bg-slate-100 py-2 px-3 text-[12px] font-semibold text-charcoal-900 transition-colors hover:bg-slate-200 dark:bg-charcoal-800 dark:text-slate-100 dark:hover:bg-charcoal-700"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" />
          {t('navigation:new_shop', { defaultValue: 'Nouvelle boutique' })}
        </Link>
        <Link
          to={ownerPath('/shops')}
          onClick={handleActionClick}
          className="flex items-center justify-between rounded-lg px-3 py-1.5 text-[12px] text-slate-600 transition-colors hover:bg-slate-100 hover:text-charcoal-950 dark:text-slate-400 dark:hover:bg-charcoal-800 dark:hover:text-slate-200"
        >
          <span className="flex items-center gap-2">
            <Store className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
            {t('navigation:manage_shops', { defaultValue: 'Gérer mes boutiques' })}
          </span>
          <span className="text-[10px] font-mono text-slate-400">({shops.length})</span>
        </Link>
      </div>
    </div>
  );

  // ============================================================
  // RENDU MOBILE (< 1024px) : BOTTOM SHEET NATIF
  // ============================================================
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>{triggerButton}</SheetTrigger>
        <OwnerSheetContent
          side="bottom"
          showCloseButton
          className="rounded-t-3xl max-h-[85vh] p-0 flex flex-col overflow-hidden"
        >
          {/* Poignée de drag handle */}
          <div aria-hidden className="flex justify-center pt-3 pb-1">
            <span className="h-1.5 w-12 rounded-full bg-slate-300 dark:bg-charcoal-600" />
          </div>

          {/* En-tête du Bottom Sheet */}
          <div className="flex items-start justify-between px-5 pt-2 pb-3">
            <div>
              <SheetTitle className="font-head text-[16px] font-bold text-charcoal-950 dark:text-slate-50">
                {t('navigation:shop_switcher', { defaultValue: 'Changer de boutique' })}
              </SheetTitle>
              <SheetDescription className="text-[12px] text-slate-500 dark:text-slate-400">
                {shops.length}{' '}
                {shops.length > 1
                  ? t('navigation:shops_count_plural', { defaultValue: 'boutiques enregistrées' })
                  : t('navigation:shops_count_singular', { defaultValue: 'boutique enregistrée' })}
              </SheetDescription>
            </div>
          </div>

          {/* Corps défilable */}
          <div className="flex-1 overflow-y-auto px-4 pb-6 pt-1">{listContent}</div>
        </OwnerSheetContent>
      </Sheet>
    );
  }

  // ============================================================
  // RENDU DESKTOP (≥ 1024px) : POPOVER PRO
  // ============================================================
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <OwnerPopoverContent
        side="right"
        align="end"
        sideOffset={14}
        className="w-84 p-2.5 shadow-xl"
      >
        <div className="mb-2 flex items-center justify-between px-1 pt-0.5">
          <p className="font-head text-[13px] font-bold text-charcoal-950 dark:text-slate-50">
            {t('navigation:shops_section', { defaultValue: 'Boutiques' })}{' '}
            <span className="text-[11px] font-normal text-slate-400">({shops.length})</span>
          </p>
          <Link
            to={ownerPath('/shops?create=1')}
            onClick={handleActionClick}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-lime-700 hover:bg-lime-50 dark:text-lime-400 dark:hover:bg-lime-500/10"
          >
            <Plus className="h-3 w-3" strokeWidth={2.5} />
            {t('navigation:create_shop', { defaultValue: 'Créer' })}
          </Link>
        </div>
        {listContent}
      </OwnerPopoverContent>
    </Popover>
  );
}
